import maxBy from 'lodash/maxBy';

import { getUniqueRoomAssets } from '~/components/Offers/Detail/OfferBuilder/Assets/utils';

import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { isAfter } from '~/services/TimeService';

import { stringSimilarity } from '~/utils/stopsellRoomMatching';

export function dayCompareFunc(a: { date: string | Date }, b: { date: string | Date }) {
  if (isAfter(b.date, a.date)) {
    return -1;
  }
  if (isAfter(a.date, b.date)) {
    return 1;
  }
  return 0;
}

export function getOfferPropertyId(offer: App.Offer | undefined): string | undefined {
  return offer?.packages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id;
}

const MIN_NAME_MATCH_THRESHOLD = 0.75;

function normaliseRoomName(name: string) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface PartnerSubmittedRoomAssetMatch {
  roomAsset: ReturnType<typeof getUniqueRoomAssets>[number];
  isExactMatch: boolean;
}

export function getPartnerSubmittedRoomAsset(
  roomTypeName: string,
  deals: Array<OpportunityRelatedDeal> | undefined,
): PartnerSubmittedRoomAssetMatch | undefined {
  const roomAssets = getUniqueRoomAssets(deals);
  if (roomAssets.length === 0) {
    return undefined;
  }

  const normalisedRoomTypeName = normaliseRoomName(roomTypeName);
  const exactMatch = roomAssets.find((roomAsset) => normaliseRoomName(roomAsset.name ?? '') === normalisedRoomTypeName);
  if (exactMatch) {
    return { roomAsset: exactMatch, isExactMatch: true };
  }

  const bestRoomAsset = maxBy(roomAssets, (roomAsset) => stringSimilarity(roomTypeName, roomAsset.name ?? ''));
  const bestScore = stringSimilarity(roomTypeName, bestRoomAsset.name ?? '');

  return bestScore >= MIN_NAME_MATCH_THRESHOLD ? { roomAsset: bestRoomAsset, isExactMatch: false } : undefined;
}

export interface SalesforceRoomAllocation {
  allocation: number | undefined;
  rawAllocation: string | undefined;
  isInvalid: boolean;
}

export function getSalesforceRoomAllocation(
  roomTypeName: string,
  deals: Array<OpportunityRelatedDeal> | undefined,
): SalesforceRoomAllocation {
  const rawAllocation = getSalesforceDeal(roomTypeName, deals)?.allocation__c;
  if (rawAllocation == null) {
    return { allocation: undefined, rawAllocation: undefined, isInvalid: false };
  }
  const allocation = Number(rawAllocation);
  return Number.isFinite(allocation)
    ? { allocation, rawAllocation: String(rawAllocation), isInvalid: false }
    : { allocation: undefined, rawAllocation: String(rawAllocation), isInvalid: true };
}

function getSalesforceDeal(
  roomTypeName: string,
  deals: Array<OpportunityRelatedDeal> | undefined,
): OpportunityRelatedDeal | undefined {
  const matchedRoomAsset = getPartnerSubmittedRoomAsset(roomTypeName, deals)?.roomAsset;
  return matchedRoomAsset?.sfid ? deals?.find((deal) => deal.room_asset?.sfid === matchedRoomAsset.sfid) : undefined;
}

export function getSalesforceMaxOccupancy(
  roomTypeName: string,
  deals: Array<OpportunityRelatedDeal> | undefined,
): string | undefined {
  return getPartnerSubmittedRoomAsset(roomTypeName, deals)?.roomAsset.max_occupancy__c ?? undefined;
}

export function getSalesforceBedding(
  roomTypeName: string,
  deals: Array<OpportunityRelatedDeal> | undefined,
): string | undefined {
  return getPartnerSubmittedRoomAsset(roomTypeName, deals)?.roomAsset.standard_bedding_configuration__c ?? undefined;
}

export function getSalesforceTravelDates(keyDates: App.KeyDates | undefined): {
  startDate: string | undefined;
  endDate: string | undefined;
} {
  return { startDate: keyDates?.expiry_date_travel_from__c, endDate: keyDates?.travel_to__c };
}
